/**
 * Vendor documentation: http://www.plupload.com/docs
 *
 * Events:
 *
 * MediaUpload:filePickerOpen
 * MediaUpload:filesAdded {files}
 * MediaUpload:uploadStart {files}
 * MediaUpload:fileUploaded {file, response}
 * MediaUpload:uploadComplete {files}
 * MediaUpload:filesRemoved {files}
 * MediaUpload:error {error}
 */
import View from 'view/view'

import template from 'component/uploader/template/media_upload.tmpl'

import plupload from 'plupload'
import uploaderService from 'service/uploader'

export default View.extend({
    template,

    uploader: null,

    defaultOptions: {
        title: 'Add photos',
        multi_selection: true,
    },

    initialize(options) {
        this.options = this.pickOptions(options, this.defaultOptions)

        this.uploader = uploaderService.create({
            browse_button: this.el,
            multi_selection: options.multi_selection,
        })

        // listen to pluploader events
        this.listenTo(this.uploader, 'PostInit', this._onUploaderPostInit)
        this.listenTo(this.uploader, 'Browse', this._onUploaderBrowse)
        this.listenTo(this.uploader, 'BeforeFilesResolve', this._onBeforeFilesResolve)
        this.listenTo(this.uploader, 'FilesAdded', this._onUploaderFilesAdded)
        this.listenTo(this.uploader, 'FileUploaded', this._onUploaderFileUploaded)
        this.listenTo(this.uploader, 'UploadProgress', this._onUploaderUploadProgress)
        this.listenTo(this.uploader, 'FilesRemoved', this._onUploaderFilesRemoved)
        this.listenTo(this.uploader, 'UploadComplete', this._onUploaderUploadComplete)
        this.listenTo(this.uploader, 'Error', this._onUploaderError)

        this.uploader.init()
    },

    // Fires when the current RunTime has been initialized.
    _onUploaderPostInit(/* uploader */) {},

    // Fires when the FilePicker dialog opens
    _onUploaderBrowse() {
        this.trigger('MediaUpload:filePickerOpened')
    },

    _onBeforeFilesResolve() {
        this.trigger('MediaUpload:beforeFilesResolve')
    },

    _onUploaderFilesAdded(uploader, files) {
        this.trigger('MediaUpload:filesAdded', { files })

        if (uploader.runtime !== 'flash') {
            uploader.start()
            this.trigger('MediaUpload:uploadStart', { files })
            return
        }

        // We need to wait some time to give flash a chance to be ready for starting the upload. This horrible
        // arbitrary number simply 'seems to work'. I sooth myself by thinking this is also more or less the time
        // an user takes to go to a 'start upload' button after selecting files :S
        setTimeout(() => {
            uploader.start()
            this.trigger('MediaUpload:uploadStart', { files })
        }, 1000)
    },

    // Fires while a file is being uploaded. Use this event to update the current file upload progress.
    _onUploaderUploadProgress(/* uploader, file */) {},

    // Fires when a file is successfully uploaded.
    _onUploaderFileUploaded(uploader, file, response) {
        const { result, error } = JSON.parse(response.response)

        if (error) {
            error.file = file
            this.trigger('MediaUpload:error', { error })
            return
        }

        this.trigger('MediaUpload:fileUploaded', { file, response: result })
    },

    // Fires when file is removed from the queue
    _onUploaderFilesRemoved(uploader, files) {
        this.trigger('MediaUpload:filesRemoved', { files })
    },

    // files queue is uploaded
    _onUploaderUploadComplete(uploader, files) {
        this.trigger('MediaUpload:uploadComplete', { files })
    },

    _onUploaderError(uploader, error) {
        if (error.code === plupload.INIT_ERROR) {
            error.message = `Please install a <a href="http://browsehappy.com/?locale=en" target="_blank">
modern browser</a> to be able to upload files.`
        }

        // file uploading errors
        if (error.code === plupload.FILE_SIZE_ERROR) {
            error.message = `<b>${error.file.name}</b> failed to upload.<br/>Please upload an image with less
than 15MB file size.`
        }

        if (error.code === plupload.IMAGE_DIMENSIONS_ERROR) {
            error.message = `<b>${error.file.name}</b> failed to upload.<br/>Please upload an image with less
than 15000x15000px resolution.<br /><b><em>Tip:</em></b>
Use a photo editor to make the image smaller and try again.`
        }

        if (!error.message) {
            error.message = 'Unknown error'
        }

        this.trigger('MediaUpload:error', { error })
    },

    removeFile(fileId) {
        this.uploader.removeFile(fileId)
        return this
    },

    remove(...args) {
        this.uploader.destroy()
        return View.prototype.remove.apply(this, args)
    },

    render() {
        this.$el.html(
            this.template({
                title: this.options.title,
            })
        )
        return this
    },
})
